import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { PlayArrow, Science } from '@mui/icons-material';
import ApiContext from 'contexts/ApiContext';

/**
 * Run a rule against a real past transaction before it goes live.
 *
 * Recommended, not required: it turns "I think this rule is right" into "I
 * watched it run on transaction #4417". Nothing is persisted except the
 * fingerprint of what was tested — the form reads that to show a "Not tested"
 * hint, and editing the rule after a preview brings the hint back.
 *
 * The backtest below it is advisory: a rule that flags 40 of 50 past
 * transactions is almost certainly inverted, and the spread makes that obvious
 * at a glance in a way re-reading the rule never does.
 */

interface PreviewPanelProps {
  teamUid: string;
  /** The working copy, exactly as it would be saved. */
  rule: any;
  /** Blocks previewing a rule that can't run at all. */
  disabled?: boolean;
  /**
   * Bumped by the parent to re-run the last test. Used right after a rule's
   * first save: the earlier preview had no saved rule to be recorded against,
   * so the rule would still read as untested.
   */
  rerunToken?: number;
  onPreviewed?: (outcome: string) => void;
}

const OUTCOME_LABEL: Record<string, string> = { true: 'Match', false: 'No Match', other: 'Inconclusive' };
const OUTCOME_COLOR: Record<string, 'success' | 'error' | 'warning'> = {
  true: 'success',
  false: 'error',
  other: 'warning'
};

const PreviewPanel: React.FC<PreviewPanelProps> = ({ teamUid, rule, disabled, rerunToken = 0, onPreviewed }) => {
  const apiContext = useContext(ApiContext);

  const [candidates, setCandidates] = useState<any[]>([]);
  const [selected, setSelected] = useState('');
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const [backtesting, setBacktesting] = useState(false);
  const [backtest, setBacktest] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!apiContext || !teamUid || disabled) return;
      setLoadingCandidates(true);
      try {
        const res: any = await apiContext.previewDetectionCandidates(teamUid, rule, 15);
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        setCandidates(list);
        // Default to a transaction that actually carries every step the rule
        // reads — otherwise the first preview says "waiting on…" and teaches
        // the author nothing.
        setSelected((prev) => prev || list.find((c: any) => c.hasAllSteps)?.uid || list[0]?.uid || '');
      } catch {
        if (!cancelled) setCandidates([]);
      } finally {
        if (!cancelled) setLoadingCandidates(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // Only re-fetch when the rule's step bindings could have changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamUid, disabled, JSON.stringify(rule?.spec?.inputs ?? rule?.sources ?? [])]);

  const run = async () => {
    if (!apiContext || !selected) return;
    setRunning(true);
    setError(null);
    // Deliberately NOT clearing `result` here: blanking it mid-run makes the
    // panel flash empty, which reads as "the result vanished".
    try {
      const res: any = await apiContext.previewDetectionRule(teamUid, rule, selected);
      if (res?.success === false) {
        setError(typeof res?.message === 'string' ? res.message : 'Preview failed');
        return;
      }
      setResult(res?.data);
      if (res?.data?.outcome) onPreviewed?.(res.data.outcome);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Preview failed');
    } finally {
      setRunning(false);
    }
  };

  // Re-run on the parent's nudge, keeping the visible result in place until the
  // new one lands so the panel never flashes empty.
  const firstToken = React.useRef(rerunToken);
  useEffect(() => {
    if (rerunToken === firstToken.current) return;
    firstToken.current = rerunToken;
    if (selected && result) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerunToken]);

  const runBacktest = async () => {
    if (!apiContext) return;
    setBacktesting(true);
    setBacktest(null);
    try {
      const res: any = await apiContext.backtestDetectionRule(teamUid, rule, 25);
      if (res?.success === false) {
        setError(typeof res?.message === 'string' ? res.message : 'Backtest failed');
        return;
      }
      setBacktest(res?.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Backtest failed');
    } finally {
      setBacktesting(false);
    }
  };

  if (disabled) {
    return (
      <Alert severity="info" sx={{ py: 0.5 }}>
        <Typography variant="caption">Finish the rule above, then test it against a real transaction.</Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <FormControl size="small" sx={{ minWidth: 260, flex: 1 }}>
          <InputLabel>Test against</InputLabel>
          <Select value={selected} label="Test against" onChange={(e) => setSelected(e.target.value)} disabled={loadingCandidates}>
            {candidates.length === 0 ? (
              <MenuItem disabled>
                <em>{loadingCandidates ? 'Loading…' : 'No transactions on this process yet'}</em>
              </MenuItem>
            ) : (
              candidates.map((c) => (
                <MenuItem key={c.uid} value={c.uid}>
                  {c.key || c.uid}
                  {!c.hasAllSteps && ' — missing some steps'}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={running ? <CircularProgress size={14} /> : <PlayArrow />}
          onClick={run}
          disabled={!selected || running}
        >
          {running ? 'Running…' : 'Run preview'}
        </Button>
        <Button
          variant="outlined"
          startIcon={backtesting ? <CircularProgress size={14} /> : <Science />}
          onClick={runBacktest}
          disabled={backtesting}
        >
          {backtesting ? 'Checking…' : 'Check last 25'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
          <Typography variant="caption">{error}</Typography>
        </Alert>
      )}

      {result && (
        <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 1.5, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip
              label={OUTCOME_LABEL[result.outcome] || result.outcome}
              size="small"
              color={OUTCOME_COLOR[result.outcome] || 'default'}
              sx={{ fontWeight: 700 }}
            />
            <Typography variant="caption" color="text.secondary">
              {result.transactionKey} · {result.durationMs}ms · {result.engine === 'deterministic' ? 'exact' : 'AI'}
            </Typography>
          </Box>

          {/* Which real values it read — the most common cause of a surprising
              result is a source bound to the wrong field. */}
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            Inputs it read
          </Typography>
          {(result.inputs || []).map((i: any) => (
            <Box key={i.slot} sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mb: 0.25 }}>
              <Typography sx={{ fontSize: '0.72rem', color: i.resolved ? 'success.main' : 'warning.main' }}>
                {i.resolved ? '✓' : '—'}
              </Typography>
              <Typography variant="caption" sx={{ minWidth: 120, color: 'text.secondary' }}>
                {i.label}
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
                {i.display}
              </Typography>
            </Box>
          ))}

          {result.aiMessage && (
            <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem' }}>
              {result.aiMessage}
            </Typography>
          )}

          {(result.evidence || []).length > 0 && (
            <Box sx={{ mt: 0.75 }}>
              {result.evidence.map((ev: any, i: number) => (
                <Typography
                  key={i}
                  variant="caption"
                  sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary' }}
                >
                  {ev.supports === 'pass' ? '✓' : '✕'} {ev.observation}
                </Typography>
              ))}
            </Box>
          )}

          {result.deferred && (
            <Alert severity="warning" sx={{ mt: 1, py: 0.25 }}>
              <Typography variant="caption">
                That transaction is missing a step this rule reads. Pick one that has them all for a real answer.
              </Typography>
            </Alert>
          )}
        </Box>
      )}

      {backtest && (
        <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, p: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>
            Across {backtest.evaluated} past transaction{backtest.evaluated === 1 ? '' : 's'}
            {backtest.skipped > 0 && ` (${backtest.skipped} skipped — missing steps)`}
          </Typography>
          {['true', 'false', 'other'].map((k) => {
            const count = backtest.counts?.[k] || 0;
            const pct = backtest.evaluated ? (count / backtest.evaluated) * 100 : 0;
            return (
              <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                <Typography variant="caption" sx={{ minWidth: 90 }}>
                  {OUTCOME_LABEL[k]}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  color={OUTCOME_COLOR[k]}
                  sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'grey.200' }}
                />
                <Typography variant="caption" sx={{ minWidth: 30, textAlign: 'right' }}>
                  {count}
                </Typography>
              </Box>
            );
          })}

          {/* A rule that flags nearly everything is almost always inverted. */}
          {backtest.evaluated >= 5 && (backtest.counts?.false || 0) / backtest.evaluated > 0.6 && (
            <Alert severity="warning" sx={{ mt: 1, py: 0.25 }}>
              <Typography variant="caption">
                This flags {Math.round(((backtest.counts.false || 0) / backtest.evaluated) * 100)}% of past transactions. Worth checking the
                outcome isn’t inverted.
              </Typography>
            </Alert>
          )}

          {(backtest.samples || []).length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Examples that didn’t match
              </Typography>
              {backtest.samples.map((s: any, i: number) => (
                <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}>
                  {s.transactionKey}: {s.message}
                </Typography>
              ))}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PreviewPanel;
